import React, { useState, useRef } from 'react';
import { 
  Delete, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Package, 
  Store,
  QrCode,
  Scan,
  ShieldCheck,
  Clock,
  Phone,
  MessageCircle,
  HelpCircle,
  Crown
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

type DeliveryProvider = 'fash' | 'uber';

interface PickupItem {
  name: string;
  quantity: number;
}

interface PickupFormProps {
  orderId: string;
  storeName: string;
  provider?: DeliveryProvider;
  isPremium?: boolean;
  items?: PickupItem[];
  onVerify: (pin: string) => Promise<boolean>;
  onContactSupport: () => void;
  maxAttempts?: number;
}

export function PickupForm({ 
  orderId, 
  storeName, 
  provider = 'fash',
  isPremium = false,
  items = [],
  onVerify, 
  onContactSupport,
  maxAttempts = 3 
}: PickupFormProps) {
  const [pin, setPin] = useState<string[]>(['', '', '', '']);
  const [attempts, setAttempts] = useState(0);
  const [status, setStatus] = useState<'idle' | 'verifying' | 'success' | 'error' | 'locked'>('idle');
  const [showQRScanner, setShowQRScanner] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const isUber = provider === 'uber';

  const handleDigitPress = (digit: string) => {
    if (status === 'locked' || status === 'verifying' || status === 'success') return;
    
    const newPin = [...pin];
    const emptyIndex = newPin.findIndex(d => d === '');
    
    if (emptyIndex !== -1) {
      newPin[emptyIndex] = digit;
      setPin(newPin);
      
      // Auto-verify when all 4 digits entered
      if (emptyIndex === 3) {
        verifyPin(newPin.join(''));
      }
    }
  };

  const handleDelete = () => {
    if (status === 'locked' || status === 'verifying' || status === 'success') return;
    
    const newPin = [...pin];
    for (let i = newPin.length - 1; i >= 0; i--) {
      if (newPin[i] !== '') {
        newPin[i] = '';
        break;
      }
    }
    setPin(newPin);
    setStatus('idle');
  };

  const verifyPin = async (pinString: string) => {
    setStatus('verifying');
    
    try {
      const isValid = await onVerify(pinString);
      
      if (isValid) {
        setStatus('success');
        // Haptic feedback simulation
        if (navigator.vibrate) navigator.vibrate(100);
      } else {
        setStatus('error');
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= maxAttempts) {
          setStatus('locked');
        } else {
          // Reset after error animation
          setTimeout(() => {
            setPin(['', '', '', '']);
            setStatus('idle');
          }, 1500);
        }
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => {
        setPin(['', '', '', '']);
        setStatus('idle');
      }, 1500);
    }
  };

  const keypadDigits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'delete'];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="p-6 text-center border-b border-gray-100">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <span className={cn(
            "text-xs px-2 py-0.5 rounded-full font-medium",
            isUber ? "bg-black text-white" : "bg-purple-100 text-purple-700"
          )}>
            {isUber ? 'Uber' : 'Fash'}
          </span>
          {isPremium && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 flex items-center">
              <Crown className="h-3 w-3 mr-1" />
              Priority
            </span>
          )}
        </div>
        <p className="text-sm text-body uppercase tracking-wider">Pickup Verification</p>
        <h1 className="text-xl font-serif text-heading mt-1">{storeName}</h1>
        <p className="text-sm text-body mt-1">Order #{orderId.slice(-6)}</p>
      </div>

      {/* Items to pick up */}
      {items.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
          <p className="text-sm font-medium text-heading mb-2 flex items-center">
            <Package className="h-4 w-4 mr-2 text-body" />
            Items to pick up ({items.length})
          </p>
          <div className="space-y-1">
            {items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-body">{item.name}</span>
                <span className="text-heading font-medium">×{item.quantity}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PIN Display */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className={cn(
          "w-16 h-16 rounded-full flex items-center justify-center mb-4",
          isUber ? "bg-gray-900" : "bg-purple-100"
        )}>
          <ShieldCheck className={cn(
            "h-8 w-8",
            isUber ? "text-white" : "text-primary"
          )} />
        </div>
        
        <p className="text-body mb-6 text-center max-w-xs">
          Enter the 4-digit code shown to the store staff
        </p>
        
        <motion.div 
          className="flex space-x-4"
          animate={status === 'error' ? { x: [0, -10, 10, -10, 10, 0] } : {}}
          transition={{ duration: 0.4 }}
        >
          {pin.map((digit, idx) => (
            <div
              key={idx}
              className={cn(
                "w-16 h-20 rounded-2xl border-2 flex items-center justify-center text-3xl font-bold transition-all duration-200",
                digit 
                  ? status === 'success'
                    ? "border-success bg-success/10 text-success"
                    : status === 'error'
                      ? "border-error bg-error/10 text-error"
                      : isUber
                        ? "border-gray-900 bg-gray-50 text-heading"
                        : "border-primary bg-primary/5 text-heading"
                  : "border-gray-200 bg-gray-50"
              )}
            >
              {digit ? '•' : ''}
            </div>
          ))}
        </motion.div>

        {/* Status Message */}
        <AnimatePresence mode="wait">
          {status === 'verifying' && (
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 text-body flex items-center"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                className="mr-2"
              >
                <Clock className="h-4 w-4" />
              </motion.div>
              Verifying...
            </motion.p>
          )}
          
          {status === 'success' && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-6 flex items-center space-x-2 text-success"
            >
              <CheckCircle className="h-6 w-6" />
              <span className="font-medium">Verified! Starting delivery...</span>
            </motion.div>
          )}
          
          {status === 'error' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 flex items-center space-x-2 text-error"
            >
              <XCircle className="h-6 w-6" />
              <span className="font-medium">Incorrect code. {maxAttempts - attempts} attempts left.</span>
            </motion.div>
          )}
          
          {status === 'locked' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 text-center"
            >
              <div className="flex items-center justify-center space-x-2 text-warning mb-4">
                <AlertTriangle className="h-6 w-6" />
                <span className="font-medium">Too many attempts</span>
              </div>
              <p className="text-sm text-body mb-4">Please contact support to verify this pickup.</p>
              <div className="flex space-x-3">
                <Button onClick={onContactSupport} variant="outline" className="flex-1">
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
                <Button onClick={onContactSupport} variant="outline" className="flex-1">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Chat
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* QR Scanner Option (Uber feature) */}
        {isUber && status === 'idle' && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={() => setShowQRScanner(true)}
            className="mt-6 flex items-center space-x-2 text-body hover:text-heading transition-colors"
          >
            <QrCode className="h-5 w-5" />
            <span className="text-sm font-medium">Scan QR code instead</span>
          </motion.button>
        )}
      </div>

      {/* Keypad */}
      {status !== 'locked' && status !== 'success' && (
        <div className={cn(
          "p-6",
          isUber ? "bg-gray-900" : "bg-gray-50"
        )}>
          <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
            {keypadDigits.map((key, idx) => {
              if (key === '') return <div key={idx} />;
              
              if (key === 'delete') {
                return (
                  <button
                    key={idx}
                    onClick={handleDelete}
                    className={cn(
                      "h-16 rounded-2xl flex items-center justify-center transition-colors",
                      isUber 
                        ? "bg-gray-800 hover:bg-gray-700 active:bg-gray-600" 
                        : "bg-gray-200 hover:bg-gray-300 active:bg-gray-400"
                    )}
                  >
                    <Delete className={cn(
                      "h-6 w-6",
                      isUber ? "text-white" : "text-heading"
                    )} />
                  </button>
                );
              }
              
              return (
                <button
                  key={idx}
                  onClick={() => handleDigitPress(key)}
                  className={cn(
                    "h-16 rounded-2xl text-2xl font-medium transition-colors",
                    isUber 
                      ? "bg-gray-800 text-white hover:bg-gray-700 active:bg-gray-600" 
                      : "bg-white shadow-sm text-heading hover:bg-gray-50 active:bg-gray-100"
                  )}
                >
                  {key}
                </button>
              );
            })}
          </div>
          
          {/* Help text */}
          <p className={cn(
            "text-center text-xs mt-4",
            isUber ? "text-gray-500" : "text-body"
          )}>
            Ask the store for the pickup code
          </p>
        </div>
      )}

      {/* QR Scanner Modal */}
      <AnimatePresence>
        {showQRScanner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex flex-col"
          >
            <div className="p-4 flex items-center justify-between">
              <button 
                onClick={() => setShowQRScanner(false)}
                className="text-white p-2"
              >
                <XCircle className="h-6 w-6" />
              </button>
              <span className="text-white font-medium">Scan QR Code</span>
              <button className="text-white p-2">
                <HelpCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 flex items-center justify-center">
              <div className="relative">
                <div className="w-64 h-64 border-2 border-white/50 rounded-3xl">
                  <div className="absolute inset-4 border-2 border-white rounded-2xl" />
                </div>
                <motion.div
                  className="absolute inset-x-4 h-0.5 bg-white"
                  animate={{ top: ['16px', 'calc(100% - 16px)', '16px'] }}
                  transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
                />
              </div>
            </div>
            
            <div className="p-6 text-center">
              <p className="text-white/80 text-sm mb-4">
                Position the QR code within the frame
              </p>
              <Button 
                variant="outline" 
                onClick={() => setShowQRScanner(false)}
                className="border-white/30 text-white hover:bg-white/10"
              >
                Enter code manually
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
